import { useEffect, useState } from 'react';
import { supportApi } from '../api/Supportapi';

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-600',
  assigned: 'bg-amber-50 text-amber-600',
  in_progress: 'bg-purple-50 text-purple-600',
  resolved: 'bg-emerald-50 text-emerald-600',
  closed: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  new: 'New',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

export default function TicketDetail({ ticketId, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    supportApi
      .getTicket(ticketId)
      .then(setTicket)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [ticketId]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await supportApi.replyToTicket(ticketId, message.trim());
      setMessage('');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-purple-600 mb-5">
        <ArrowLeftIcon />
        Back to My Tickets
      </button>

      {loading && <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>}
      {error && <div className="bg-red-50 text-red-600 text-sm rounded-xl p-3 mb-4 border border-red-100">{error}</div>}

      {ticket && (
        <>
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 mb-5">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-[10px] font-semibold text-purple-600 uppercase">{ticket.ticket_number}</span>
              <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${STATUS_STYLES[ticket.status]}`}>
                {STATUS_LABELS[ticket.status]}
              </span>
            </div>
            <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">{ticket.subject}</h3>
            <p className="text-xs text-gray-400 mb-3">{ticket.category}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{ticket.description}</p>
          </div>

         <div className="bg-white border border-gray-100 rounded-2xl shadow-sm mb-5 overflow-hidden">
  <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
    <p className="text-xs font-semibold text-gray-600">Conversation</p>
    <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
      {ticket.replies?.length || 0} messages
    </span>
  </div>

  <div className="p-5 flex flex-col gap-4 max-h-[420px] overflow-y-auto">
    {ticket.replies?.map((r) => {
      // Anything that isn't the ticket owner's own reply is staff (agent or admin)
      const isStaff = r.sender_role === 'agent' || r.sender_role === 'admin';
      const name = isStaff ? (r.sender?.name || 'Support') : 'You';
      return (
        <div key={r.id} className={`flex items-end gap-2.5 ${isStaff ? '' : 'flex-row-reverse'}`}>
          <div className={`shrink-0 w-8 h-8 rounded-full text-white text-xs font-semibold flex items-center justify-center ${isStaff ? 'bg-gray-400' : 'bg-gradient-to-br from-purple-600 to-pink-500'}`}>
            {name.trim().charAt(0).toUpperCase()}
          </div>
          <div className={`max-w-[75%] flex flex-col ${isStaff ? 'items-start' : 'items-end'}`}>
            <p className="text-[10px] text-gray-400 mb-1 px-1">
              {name}
              {r.created_at && ` · ${new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
            </p>
            <div
              className={`px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                isStaff
                  ? 'bg-purple-50/70 text-gray-700 border border-purple-100 rounded-2xl rounded-bl-sm'
                  : 'bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-2xl rounded-br-sm'
              }`}
            >
              {r.message}
            </div>
          </div>
        </div>
      );
    })}
    {(!ticket.replies || ticket.replies.length === 0) && (
      <div className="text-center py-8">
        <p className="text-2xl mb-1">💬</p>
        <p className="text-xs text-gray-400">No replies yet. Our team will respond soon.</p>
      </div>
    )}
  </div>

  {ticket.status !== 'closed' ? (
    <form onSubmit={handleReply} className="border-t border-gray-100 bg-gray-50/60 p-4 flex items-end gap-3">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={2}
        placeholder="Type a reply..."
        className="flex-1 border border-gray-200 rounded-2xl px-4 py-2.5 text-sm text-[#0D1B3D] bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none"
      />
      <button
        type="submit"
        disabled={sending || !message.trim()}
        className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 disabled:opacity-50 text-white text-sm font-semibold px-6 py-2.5 rounded-full shadow-sm transition shrink-0"
      >
        {sending ? '...' : 'Send ➤'}
      </button>
    </form>
  ) : (
    <p className="border-t border-gray-100 bg-gray-50/60 text-xs text-gray-400 text-center py-3">This ticket is closed.</p>
  )}
</div>
        </>
      )}
    </div>
  );
}